import {
  AnalyticEvent,
  MONEY_VALUES_MULTIPLIER,
  match,
  useGetReservationsQuery,
  usePostHog,
  V2CheckoutFragment,
} from "@stepapp/core";
import { ReservationStatus } from "@stepapp/core/apiTypes";
import { useCallback, useMemo } from "react";
import { AppStorage } from "../services/AppStorage";
import { useGTMDispatch } from "../services/GTM";
import { useAppSelector } from "./redux";

export const useSendGTMData = () => {
  const postHog = usePostHog();
  const sendGTMData = useGTMDispatch();
  const countryCode = AppStorage().get("countryCode");
  const { id: userId, profile, firstName, lastName, email } = useAppSelector(({ user }) => user);

  const { data: reservations } = useGetReservationsQuery({
    variables: { filter: { status: [ReservationStatus.Completed] }, isCustomerApp: true },
    skip: !userId,
    fetchPolicy: "cache-first",
  });

  const reservationCount = reservations?.listReservations?.length || 0;

  const userInfo = useMemo(
    () => ({
      phone: profile?.phoneNumber,
      firstName,
      lastName,
      email,
      isReturningCustomer: reservationCount > 0,
      totalTransactionValue:
        reservations?.listReservations?.reduce(
          (acc, reservation) => acc + (reservation?.price || 0) / MONEY_VALUES_MULTIPLIER,
          0,
        ) || 0,
      reservationCount: reservationCount,
    }),
    [profile?.phoneNumber, firstName, lastName, email, reservations, reservationCount],
  );

  const sendEvent = useCallback(
    ({
      checkout,
      eventType,
    }: {
      checkout?: V2CheckoutFragment;
      eventType: (typeof AnalyticEvent.GTM)[keyof typeof AnalyticEvent.GTM];
    }) => {
      const price = checkout?.priceAfterDiscounts || 0;
      const currency = checkout?.currency || "";

      const userAddress = {
        city: checkout?.address?.city || "",
        postalCode: checkout?.address?.postalOrZipCode || "",
        street: checkout?.address?.streetAddress1 || "",
        country: countryCode,
      };

      const basicAnswersData =
        checkout?.attributes?.flatMap((attribute) => {
          return attribute?.values?.map((value) => ({
            item_id: value?.id,
            item_name: value?.name,
            description: attribute?.attribute?.name,
            currency,
            purchase_type: checkout?.periodicity?.description || "",
            price: (attribute?.price || 0) / MONEY_VALUES_MULTIPLIER,
            quantity: 1,
          }));
        }) || [];

      const selectionData =
        checkout?.services?.flatMap((service) => {
          return service?.variants?.map((variant) => ({
            item_id: service?.service?.id,
            item_name: service?.service?.name,
            description: service?.service?.description,
            currency,
            purchase_type: checkout?.periodicity?.description || "",
            price: (variant?.price || 0) / MONEY_VALUES_MULTIPLIER,
            quantity: variant?.quantity || 1,
          }));
        }) || [];

      const ecommerce = {
        transactionId: checkout?.id || "",
        total_price: price / MONEY_VALUES_MULTIPLIER,
        currency,
        price,
        value: price,
        items: [
          {
            item_id: checkout?.bundle?.id || "",
            item_name: checkout?.bundle?.name || "",
            currency,
            purchase_type: checkout?.periodicity?.description || "",
            price: price / MONEY_VALUES_MULTIPLIER,
            quantity: 1,
            discount: checkout?.periodicity?.discount || "",
          },
          ...basicAnswersData,
          ...selectionData,
        ],
      };

      const userData = { ...userAddress, ...userInfo };

      sendGTMData({
        event: eventType,
        ecommerce,
        userData,
      });

      match(eventType)
        .with(AnalyticEvent.GTM.AddToCart, () => {
          postHog?.capture(AnalyticEvent.GTM.ViewItem, {
            ecommerce,
            userData,
          });
        })
        .with(AnalyticEvent.GTM.InitiateCheckout, () => {
          postHog?.capture(AnalyticEvent.GTM.InitiateCheckout, {
            ecommerce,
            userData,
          });
          postHog?.capture(AnalyticEvent.Reservation.Summary, ecommerce);
        })
        .with(AnalyticEvent.GTM.Subscription, () => {
          postHog?.capture(AnalyticEvent.GTM.Purchase, {
            ecommerce,
            userData,
          });
          postHog?.capture(AnalyticEvent.Payment.Success);
        })
        .with(AnalyticEvent.GTM.Purchase, () => {
          postHog?.capture(AnalyticEvent.GTM.Purchase, {
            ecommerce,
            userData,
          });
          postHog?.capture(AnalyticEvent.Payment.Success);
        })
        .otherwise(() => {});
    },
    [sendGTMData, postHog, userInfo, countryCode],
  );

  return sendEvent;
};
